import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const GuildContext = createContext(null);

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const GuildProvider = ({ children }) => {
    const { token, user } = useAuth();
    const [guilds, setGuilds] = useState([]);
    const [selectedGuild, setSelectedGuild] = useState(null);
    const [guildData, setGuildData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        const fetchGuilds = async () => {
            if (!token || !user) return;

            try {
                setLoading(true);
                const response = await axios.get(`${API}/guilds`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setGuilds(response.data.guilds || []);
                
                // Auto-select first guild with bot
                const botGuilds = response.data.guilds?.filter(g => g.bot_present) || [];
                if (botGuilds.length > 0) {
                    setSelectedGuild(prev => prev || botGuilds[0].id);
                }
            } catch (error) {
                console.error('Fetch guilds error:', error);
            } finally {
                setLoading(false);
            }
        };

        fetchGuilds();
    }, [token, user]);

    useEffect(() => {
        const fetchGuildData = async () => {
            if (!token || !selectedGuild) return;

            try {
                const response = await axios.get(`${API}/guilds/${selectedGuild}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setGuildData(response.data);
            } catch (error) {
                console.error('Fetch guild data error:', error);
            }
        };

        fetchGuildData();
    }, [token, selectedGuild]);

    const selectGuild = useCallback((guildId) => {
        setSelectedGuild(guildId);
        localStorage.setItem('selected_guild', guildId);
    }, []);

    return (
        <GuildContext.Provider value={{ 
            guilds, 
            selectedGuild, 
            guildData, 
            loading, 
            selectGuild,
            setGuildData
        }}>
            {children}
        </GuildContext.Provider>
    );
};

export const useGuild = () => {
    const context = useContext(GuildContext);
    if (!context) {
        throw new Error('useGuild must be used within a GuildProvider');
    }
    return context;
};
